import React, { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import paths from './paths';
import ErrorBoundary from '../components/pages/error/ErrorBoundary';
import { productRoutes } from './routes/productRoutes';
import { campaignRoutes } from './routes/campaignRoutes';
import { campaignEditRoutes } from './routes/campaignEditRoute';
import { adminRoutes } from './routes/adminRoutes';
import { paymentRoutes } from './routes/paymentRoutes';
import { orderRoutes } from './routes/orderRoutes';
import { accountRoutes } from './routes/accountRoutes';
import { customerRoutes } from './routes/customerRoutes';
import NotFound from '../components/pages/error/NotFound';
import HomeSkeleton from '../components/layout/home/HomeSkeleton';
import ReportsError from '../components/pages/error/ReportsError';
import { campaignPageSettingsRoutes } from './routes/campaignPageSettingsRoute';
import { dashboardRoutes, reportRoutes } from './routes/reportRoutes';
import { createRoute } from './utils';
import { hasNamespaceAccess } from 'utils/helper/access';

const LoginPage = lazy(() => import('../components/pages/auth/LoginPage/LoginPage'));
const ForgetPassword = lazy(() => import('../components/pages/auth/ForgetPassword/ForgetPassword'));
const ResetPassword = lazy(() => import('../components/pages/auth/ResetPassword/ResetPassword'));
const Home = lazy(() => import('../components/layout/home/Home'));

export const router = (role, abilities, defaultPath) => {
  const product = productRoutes(abilities?.products);
  const campaign = campaignRoutes(abilities?.campaigns);

  const hasPaymentAccess = role === 'admin' || hasNamespaceAccess(abilities, 'payments');
  const hasAdminAccess =
    role === 'admin' || ['customer_service_admin', 'leads_agent', 'sales_agent', 'subaccount_user'].includes(role) || hasNamespaceAccess(abilities, 'settings');

  const router = createBrowserRouter([
    {
      path: '/',
      element: (
        <Suspense fallback={<HomeSkeleton />}>
          <Home />
        </Suspense>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        {
          index: true,
          element: <Navigate to={defaultPath} />,
        },
        ...dashboardRoutes(abilities, role),
        {
          path: paths.reportError,
          element: <ReportsError />,
        },
        ...product,
        ...campaign,
        (['admin', 'customer_service_admin'].includes(role) || hasNamespaceAccess(abilities, 'campaigns')) && campaignEditRoutes(abilities?.campaigns),
        (role === 'admin' || hasNamespaceAccess(abilities, 'campaigns')) && campaignPageSettingsRoutes(abilities?.campaigns),
        hasPaymentAccess && paymentRoutes(abilities?.payments),
        ...reportRoutes(abilities, role),
        hasAdminAccess && adminRoutes(abilities),
        accountRoutes(abilities),
        ...customerRoutes(abilities?.customer),
        ...orderRoutes(abilities?.orders),
        createRoute(paths.reportsError, ReportsError),
      ],
    },
    createRoute(paths.login, LoginPage, { defaultPath }),
    createRoute(paths.forgetPassword, ForgetPassword),
    createRoute(paths.resetPassword, ResetPassword),
    createRoute('/*', NotFound),
  ]);

  return router;
};
